import React, { useState, useEffect } from "react";
import { AlertTriangle, X } from "lucide-react";

/**
 * SafetyConfirmationGate
 * ----------------------
 * Mandatory consent modal shown before any safety-sensitive picker can
 * populate inputs. Per R2c contract: per-tap re-consent (checkbox is reset on
 * every open), Continue disabled until checked, no banned-verdict copy.
 *
 * Props:
 *   open: boolean
 *   kind: "bop" | "formation"
 *   summary: ReactNode (preview rows rendered as the modal body)
 *   onCancel: () => void
 *   onConfirm: () => void
 */
const COPY = {
  bop: {
    heading: "Confirm BOP Reference Use",
    body1:
      "These are reference starting values. They are not a substitute for the rig's BOP certification sheet, the OEM datasheet, the well program, or any regulatory requirement.",
    body2: "I will verify each one against the rig's BOP certification sheet before using.",
    checkboxLabel: "I have reviewed the rig's current BOP certification sheet.",
  },
  formation: {
    heading: "Confirm Formation Template Use",
    body1:
      "This is a planning template, not your well's actual program. Verify each section against the approved drilling program before saving.",
    body2: "",
    checkboxLabel: "I have verified these sections against the approved drilling program.",
  },
};

export default function SafetyConfirmationGate({ open, kind, summary, onCancel, onConfirm }) {
  const [checked, setChecked] = useState(false);
  // Per-tap re-consent — reset on every open.
  useEffect(() => { if (open) setChecked(false); }, [open]);

  if (!open) return null;
  const copy = COPY[kind] || COPY.bop;

  return (
    <div
      data-testid="safety-confirmation-gate"
      data-kind={kind}
      role="dialog"
      aria-modal="true"
      aria-labelledby="rs-gate-heading"
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center"
      style={{ background: "rgba(8,10,14,0.78)" }}
    >
      <div
        className="w-full sm:max-w-lg max-h-[92vh] overflow-y-auto card-rs"
        style={{ borderColor: "var(--rs-amber)", borderWidth: 1 }}
      >
        <div className="flex items-start justify-between gap-3 p-4 border-b" style={{ borderColor: "var(--rs-border)" }}>
          <div className="flex items-start gap-2.5">
            <AlertTriangle className="w-4 h-4 mt-[3px] shrink-0" style={{ color: "var(--rs-amber)" }} />
            <h2 id="rs-gate-heading" className="font-heading font-bold text-base" style={{ color: "var(--rs-text)" }}>{copy.heading}</h2>
          </div>
          <button
            type="button"
            data-testid="safety-gate-close"
            onClick={onCancel}
            aria-label="Close"
            className="text-[var(--rs-text-3)] hover:text-[var(--rs-text)] transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-4 flex flex-col gap-3">
          <p className="text-[12px] leading-snug text-[var(--rs-text-2)]" data-testid="safety-gate-body-1">{copy.body1}</p>
          {copy.body2 ? (
            <p className="text-[12px] leading-snug text-[var(--rs-text-2)]" data-testid="safety-gate-body-2">{copy.body2}</p>
          ) : null}

          {summary ? (
            <div
              data-testid="safety-gate-summary"
              className="rounded-md p-3"
              style={{ background: "rgba(22,24,29,0.55)", border: "1px solid var(--rs-border)" }}
            >
              {summary}
            </div>
          ) : null}

          <label
            className="flex items-start gap-2.5 cursor-pointer select-none mt-1"
            data-testid="safety-gate-checkbox-label"
          >
            <input
              type="checkbox"
              data-testid="safety-gate-checkbox"
              checked={checked}
              onChange={(e) => setChecked(e.target.checked)}
              className="mt-0.5 h-4 w-4 accent-[var(--rs-amber)] shrink-0"
            />
            <span className="text-[12px] font-semibold leading-snug text-[var(--rs-text)]">
              {copy.checkboxLabel}
            </span>
          </label>
        </div>

        <div className="flex items-center justify-end gap-2 p-3 border-t" style={{ borderColor: "var(--rs-border)" }}>
          <button
            type="button"
            data-testid="safety-gate-cancel"
            onClick={onCancel}
            className="btn-ghost h-9 px-3 rounded-md text-[11px] label-up"
          >
            Cancel
          </button>
          <button
            type="button"
            data-testid="safety-gate-continue"
            onClick={() => checked && onConfirm()}
            disabled={!checked}
            aria-disabled={!checked}
            className="h-9 px-4 rounded-md text-[11px] label-up font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
            style={{
              background: checked ? "var(--rs-amber)" : "rgba(148,163,184,0.3)",
              color: checked ? "#0a0d12" : "var(--rs-text-3)",
            }}
          >
            Continue
          </button>
        </div>
      </div>
    </div>
  );
}
